import { firebase } from '@firebase/app';
import '@firebase/database';
import '@firebase/storage';
import 'firebase/auth';
import 'firebase/functions';
import router from '@/router';
import store from '@/store';
import axios from '@/helpers/axios-config';
import getIdToken from '@/helpers/firebase-token';
import EventBus from '@/plugins/eventBus';
import { FILTER_TYPE } from '@/views/app/dashboard/history/data/constants';
import moment from 'moment';
import websocket from '@/services/websocket';
import { SIGN_UP_STEPS } from '@/helpers/enums';

const API_URL = process.env.VUE_APP_API_URL;
const WALLET_API = process.env.VUE_APP_WALLET_API;
const SUMSUB_API = process.env.VUE_APP_SUMSUB_API;
const NOW_PAYMENT_API = process.env.VUE_APP_NOW_PAYMENT_API;

const MQ_GAME_DOMAIN = process.env.VUE_APP_MQ_GAME_DOMAIN;

const BTG_LOBBY_URL = process.env.VUE_APP_BTG_LOBBY_URL;
const FASTFIRE_URL = process.env.VUE_APP_FASTFIRE_URL;
const MBC_LOBBY_URL = process.env.VUE_APP_MBC_LOBBY_URL;
const MAXBLAST_URL = process.env.VUE_APP_MAXBLAST_URL;
const MULTIPLINKO_URL = process.env.VUE_APP_MULTIPLINKO_URL;
const PLINKO_URL = process.env.VUE_APP_PLINKO_URL;
const CHALLENGE_URL = process.env.VUE_APP_CHALLENGE_URL;

const GOOGLE_RECAPTCHA_SCRIPT =
  process.env.VUE_APP_GOOGLE_RECAPTCHA_SCRIPT + process.env.VUE_APP_GOOGLE_RECAPTCHA_SITE_KEY;
const GOOGLE_RECAPTCHA_SITE_KEY = process.env.VUE_APP_GOOGLE_RECAPTCHA_SITE_KEY;

let blinkInterval;
let prevTitle = document.title;

let orderStatusRef = null;

function callNotify(title, msg, icon) {
  new Notification(title, { body: msg, icon: icon });
}

function startTitleBlink(newTitle) {
  const currentTitle = document.title;
  blinkInterval = setInterval(() => {
    document.title = newTitle;
    setTimeout(() => {
      document.title = currentTitle;
    }, 1500);
  }, 3000);
  return currentTitle;
}

function stopTitleBlink() {
  clearInterval(blinkInterval);
  document.title = prevTitle;
}

function getFirebaseUid() {
  return firebase?.auth()?.currentUser?.uid;
}

export default {
  clearError({ commit }) {
    commit('clearError');
  },

  async sendResetPasswordSuccessEmail(store, payload) {
    const config = {
      method: 'post',
      url: API_URL + '/sendResetPasswordSuccessEmail',
      headers: {
        MQTECH_REQUEST_KEY: process.env.VUE_APP_MQ_TECH_API_KEY,
        'Content-Type': 'application/json',
      },
      data: { email: payload.email, displayName: payload.displayName },
    };
    await axios
      .request(config)
      .then(async (response) => {
        store.dispatch('changeResetPasswordDialogState', false);
        if (response.data.haserror) {
          store.dispatch('showAlertDialog', {
            title: '',
            text: response.data.message,
          });
        } else {
          store.dispatch('showAlertDialog', {
            title: 'Reset password successful',
            text: 'You can now log in and start playing.',
          });
        }
      })
      .catch((error) => {
        store.dispatch('changeResetPasswordDialogState', false);
        store.dispatch('showAlertDialog', {
          title: 'Something went wrong',
          text: error.response.data.message,
        });
      });
  },

  async sendRecoveryEmail({ dispatch }, payload) {
    const config = {
      method: 'post',
      url: API_URL + '/resetPassword',
      headers: {
        MQTECH_REQUEST_KEY: process.env.VUE_APP_MQ_TECH_API_KEY,
        'Content-Type': 'application/json',
      },
      data: { email: payload.email },
    };
    await axios
      .request(config)
      .then(async (response) => {
        dispatch('changeLoginDialogState', false);

        if (response.data.haserror) {
          dispatch('showAlertDialog', {
            title: '',
            text: response.data.message,
          });
        } else {
          dispatch('showAlertDialog', {
            title: response.data.message,
            text: 'Check your mail, please!',
          });
        }
      })
      .catch((error) => {
        dispatch('changeLoginDialogState', false);
        if (error.response?.status === 404) {
          dispatch('showAlertDialog', {
            title: 'Email Not Found',
            text: 'The provided email address is not associated with any account. Please try again.',
          });
        } else {
          dispatch('showAlertDialog', {
            title: 'Something went wrong',
            text: error.response.data.message,
          });
        }
      });
  },

  async getActiveGames({ commit }) {
    const active_games_ref = firebase.database().ref('ACTIVEGAMES');

    const snapshot = await active_games_ref.once('value');

    snapshot.forEach((childSnapshot) => {
      const value = childSnapshot.val();
      value.key = childSnapshot.key;
      commit('appendListOfActiveGames', value);
    });

    active_games_ref.on('child_added', (snapshot) => {
      const value = snapshot.val();
      value.key = snapshot.key;
      commit('appendListOfActiveGames', value);
    });

    active_games_ref.on('child_changed', (snapshot) => {
      const value = snapshot.val();
      value.key = snapshot.key;
      commit('appendListOfActiveGames', value);
    });

    active_games_ref.on('child_removed', (snapshot) => {
      const value = snapshot.val();
      value.key = snapshot.key;
      commit('removeFromListOfActiveGames', value);
    });
  },

  async autoSignIn({ commit, dispatch }) {
    try {
      await dispatch('getActiveGames');
      await dispatch('setLoggedIn', true);
      await dispatch('autoLogin');
    } catch (error) {
      commit('setError', error);
      console.error('Auto sign-in error:', error);
    }
  },

  async signUserOut({ commit, dispatch, getters }) {
    const user = firebase.auth().currentUser;

    if (!user) return;

    const isMobileResolution = typeof document !== 'undefined' ? document.documentElement?.clientWidth < 960 : false;
    // for some reason, the sidebar values for mobile/tablet and desktop are completely opposite
    if (isMobileResolution) {
      commit('SET_SIDEBAR', false);
    } else {
      commit('SET_SIDEBAR', true);
    }

    const userDisplayName = getters.getuser.displayName;

    dispatch('stopRealTimeBalance');
    dispatch('stopRealTimeActiveGames');
    dispatch('stopRealTimeRoomsPlayerNumber');
    dispatch('stopRealTimeAlerts');
    dispatch('stopRealTimeChallenges');
    dispatch('stopRealtimeFriendsList');

    if (userDisplayName) {
      dispatch('stopRealtimeReferrals', userDisplayName);
    }

    firebase
      .auth()
      .signOut()
      .then(function () {
        commit('setUser', '');
        commit('setPlayerInfo', '');
        commit('setListOfActiveGames', []);
        commit('setPlayerMode', 'MMC');

        commit('setLoadedAlerts', []);
        commit('setLoadedChallenges', []);
        commit('setFriendList', []);
        commit('setAcceptedFriendsList', []);
        commit('setRejectedFriendList', []);
        commit('setBlockedFriendList', []);
        commit('setSentFriendList', []);
        commit('setReceivedFriendList', []);
        commit('setAllUserFriends', []);
        commit('setReferrals', []);
        commit('setReferralLink', '');
        commit('setTransHistory', []);
        // commit('setBonusHistory', []);
        commit('setLastPlayedWith', []);
        // reset user balance states
        commit('setUserWithdrawableBalance', { balance: { MMC: 0, MQC: 0 } });
        commit('setUserDepositBalance', { balance: { MMC: 0, MQC: 0 } });

        if (window.location?.pathname !== '/') {
          router.push('/');
        }
        // Sign-out successful.
      })
      .catch(function (error) {
        console.error('error signing user out', error);
        commit('setError', error);
      });
  },

  // bag collection
  async updateUserProfile(state, payload) {
    const userData = payload;
    userData.sessionKey = await getIdToken();
    const data = JSON.stringify(userData);
    const config = {
      method: 'post',
      maxBodyLength: Infinity,
      url: API_URL + '/updateUserProfile',
      headers: {
        MQTECH_REQUEST_KEY: process.env.VUE_APP_MQ_TECH_API_KEY,
        'Content-Type': 'application/json',
      },
      data,
    };
    try {
      await axios.request(config);
      store.dispatch('showAlertSnackBar', {
        type: 'success',
        text: 'Preferences saved successfully',
      });
    } catch (error) {
      console.error(error);
      store.dispatch('showAlertSnackBar', {
        type: 'error',
        text: 'Error saving preferences',
      });
    }
  },

  notifyMe() {
    if (typeof Notification !== 'function' || Notification.permission === 'denied') return;
    Notification.requestPermission();
  },

  async initRealTimeChallenges({ commit }) {
    const userKey = getFirebaseUid();

    const processChallenge = async (challengeKey, value) => {
      const { message, timestamp, roomid, deleted, status, displayName } = value;

      let challenge = {
        message,
        timestamp,
        key: challengeKey,
        roomid,
        deleted,
        displayName,
      };

      if (status === 'NOT_ACCEPTED' && displayName) {
        websocket.emit('subscribeToOnlineStatus', { onlineUsers: [displayName] });
        challenge.online = false;
        commit('appendLoadedChallenges', challenge);
      }

      commit('appendLoadedChallenges', challenge);
    };

    const fetchChallenges = async () => {
      if (!userKey) return false;
      const snapshot = await firebase.database().ref('CHALLENGE_SYSTEM').child(userKey).once('value');
      const challenges = snapshot.val();

      if (challenges) {
        await Promise.all(
          Object.entries(challenges).map(([challengeKey, value]) => processChallenge(challengeKey, value)),
        );
      }
    };

    const setupRealTimeListeners = () => {
      if (!userKey) return;
      const date = Date.now();

      firebase
        .database()
        .ref('CHALLENGE_SYSTEM')
        .child(userKey)
        .orderByChild('timestamp')
        .startAt(date)
        .on('child_added', async (data) => {
          await processChallenge(data.key, data.val());
          const challenge = {
            message: data.val().message,
            timestamp: data.val().timestamp,
            key: data.key,
            roomid: data.val().roomid,
            deleted: data.val().deleted,
          };

          commit('setNewNotification', challenge);

          if (!data.val().seen && !data.val().deleted) {
            commit('appendUnseenChallenges', data.val());
            if (Notification.permission === 'granted') {
              callNotify('New challenge request received', data.val().message, null);
              prevTitle = startTitleBlink('New challenge request');
            }
          }
        });

      firebase
        .database()
        .ref('CHALLENGE_SYSTEM')
        .child(userKey)
        .on('child_changed', async (data) => {
          const challengeKey = data.key;
          const value = data.val();

          if (value.status !== 'NOT_ACCEPTED') {
            commit('appendLoadedChallenges', {
              message: value.message,
              timestamp: value.timestamp,
              key: challengeKey,
              roomid: value.roomid,
              deleted: value.deleted,
            });
          } else {
            await processChallenge(challengeKey, value);
            commit('setNewNotification', {
              message: value.message,
              timestamp: value.timestamp,
              key: challengeKey,
              roomid: value.roomid,
              deleted: value.deleted,
            });
          }

          if (value.seen) {
            commit('removeUnseenChallenge', value);
          } else if (!value.deleted) {
            commit('appendUnseenChallenges', value);
            if (Notification.permission === 'granted') {
              callNotify('New challenge request received', value.message, null);
              prevTitle = startTitleBlink('New challenge request');
            }
          }
        });
    };

    await fetchChallenges();
    setupRealTimeListeners();

    // When the page becomes visible again, refetch notifications
    const handleVisibilityChange = async () => {
      if (!document.hidden) {
        await fetchChallenges();
      }
    };

    // Listen for visibility change events
    document.addEventListener('visibilitychange', handleVisibilityChange, false);
  },

  removeNewNotification({ commit }) {
    commit('setNewNotification', null);
  },

  // stop real time alerts must be called on exit
  stopRealTimeChallenges() {
    const key = getFirebaseUid();
    firebase.database().ref('CHALLENGE_SYSTEM').child(key).off();
  },

  async initRealTimeAlerts({ commit, dispatch }) {
    const userKey = getFirebaseUid();

    const processAlert = async (alertKey, value) => {
      const { message, timestamp, type, userId, displayName, deleted, challengeKey, gameId, hostId, buyIn, coin } =
        value;

      const ONE_MONTH = 60 * 60 * 24 * 31 * 1000;
      const isOlderThanOneMonth = timestamp < Date.now() - ONE_MONTH;

      if (isOlderThanOneMonth && userKey) {
        await firebase.database().ref('ALERT_SYSTEM').child(userKey).child(alertKey).remove();
        return;
      }

      if (type === 'OPEN_VAULT' && userKey) {
        dispatch('setWalletDialog', {
          value: true,
          tab: 'purchase',
        });
        await firebase.database().ref('ALERT_SYSTEM').child(userKey).child(alertKey).remove();
        return;
      }

      let online = false;

      commit('appendLoadedAlerts', {
        message,
        timestamp,
        type,
        userId,
        key: alertKey,
        online,
        deleted,
        challengeKey,
        gameId,
        hostId,
        buyIn,
        coin,
        displayName,
      });

      if (displayName) {
        websocket.emit('subscribeToOnlineStatus', { onlineUsers: [displayName] });
      }
    };

    const fetchAlerts = async () => {
      if (!userKey || userKey !== this.state.user.id) return false;

      const snapshot = await firebase.database().ref('ALERT_SYSTEM').child(userKey).once('value');
      const alerts = snapshot.val();

      if (!alerts) return;

      await Promise.all(Object.entries(alerts).map(([alertKey, value]) => processAlert(alertKey, value)));
    };

    const setupRealTimeListeners = () => {
      if (!userKey) return false;

      const date = Date.now();

      firebase
        .database()
        .ref('ALERT_SYSTEM')
        .child(userKey)
        .orderByChild('timestamp')
        .startAt(date)
        .on('child_added', async (data) => {
          await processAlert(data.key, data.val());
          store.dispatch('showNotificationTooltip', data.val()?.message);
          commit('setNewNotification', {
            message: data.val().message,
            timestamp: data.val().timestamp,
            type: data.val().type,
            key: data.key,
          });
        });

      firebase
        .database()
        .ref('ALERT_SYSTEM')
        .child(userKey)
        .on('child_changed', async (data) => {
          await processAlert(data.key, data.val());
          commit('setNewNotification', {
            message: data.val().message,
            timestamp: data.val().timestamp,
            type: data.val().type,
            key: data.key,
          });
        });
    };

    await fetchAlerts();
    setupRealTimeListeners();

    const handleVisibilityChange = async () => {
      if (!document.hidden) {
        await fetchAlerts();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange, false);
  },

  // stop real time alerts must be called on exit
  stopRealTimeAlerts() {
    const key = getFirebaseUid();
    firebase.database().ref('ALERT_SYSTEM').child(key).off();
  },

  initRealTimeRoomsPlayerNumber({ commit }) {
    firebase
      .database()
      .ref('roomsplayernumber')
      .on('value', (snapshot) => {
        const roomsPlayerNumber = snapshot.val();
        commit('setRoomsPlayerNumber', roomsPlayerNumber);
      });
  },

  stopRealTimeRoomsPlayerNumber() {
    firebase.database().ref('roomsplayernumber').off();
  },

  stopRealTimeActiveGames() {
    firebase.database().ref('ACTIVEGAMES').off();
  },

  stopRealTimeBalance() {
    const key = getFirebaseUid();
    firebase.database().ref('users').child(key).off();
  },

  clearsinglechallenge({ commit, state }, payload) {
    const messageKey = payload;
    const userKey = getFirebaseUid();
    const newChallenges = state.challenges.map((challenge) =>
      challenge.key === messageKey ? { ...challenge, deleted: true } : challenge,
    );

    firebase.database().ref('CHALLENGE_SYSTEM').child(userKey).child(messageKey).update({ deleted: true });

    commit('setLoadedChallenges', newChallenges);
  },

  clearsinglealert(store, payload) {
    const messageKey = payload;
    const userKey = getFirebaseUid();
    firebase.database().ref('ALERT_SYSTEM').child(userKey).child(messageKey).update({ deleted: true });
  },

  clearAlerts(store, type) {
    const userKey = getFirebaseUid();

    const markAsDeleted = (snapshot, typeFilter) => {
      let updates = {};
      snapshot.forEach((notificationSnapshot) => {
        const notification = notificationSnapshot.val();
        if (
          typeFilter === 'all' ||
          (typeFilter === 'requests' && (notification.type === 'REQUEST' || notification.type === 'FRIEND_REQUEST')) ||
          notification.type === typeFilter
        ) {
          updates[notificationSnapshot.key + '/deleted'] = true;
        }
      });
      return updates;
    };

    const alertPromise = firebase
      .database()
      .ref('ALERT_SYSTEM')
      .child(userKey)
      .once('value')
      .then((alertSnapshot) => {
        let updates = markAsDeleted(
          alertSnapshot,
          type === 'rewards' ? 'REWARD' : type === 'requests' ? 'requests' : 'all',
        );
        return firebase.database().ref('ALERT_SYSTEM').child(userKey).update(updates);
      });

    const challengePromise = firebase
      .database()
      .ref('CHALLENGE_SYSTEM')
      .child(userKey)
      .once('value')
      .then((challengeSnapshot) => {
        let updates = markAsDeleted(
          challengeSnapshot,
          type === 'rewards' ? 'REWARD' : type === 'requests' ? 'requests' : 'all',
        );
        return firebase.database().ref('CHALLENGE_SYSTEM').child(userKey).update(updates);
      });

    Promise.all([alertPromise, challengePromise])
      .then(() => {})
      .catch((error) => {
        console.error('Error deleting notifications: ', error);
      });
  },
  async requestGameplayTokenAs({ commit }) {
    const token = await getIdToken();
    const data = JSON.stringify({
      USERID: token,
    });
    const config = {
      method: 'post',
      maxBodyLength: Infinity,
      url: WALLET_API + '/gentoken',
      headers: {
        MQTECH_REQUEST_KEY: process.env.VUE_APP_MQ_TECH_API_KEY,
        'Content-Type': 'application/json',
      },
      data,
    };
    return axios
      .request(config)
      .then((response) => {
        commit('setGameplayToken', response.data.EXTSYSTEM.RESPONSE.TOKEN);
        commit('setLoading', false);
      })
      .catch((error) => {
        console.error(error);
      });
  },

  async getPackages({ state, commit }) {
    if (state.packagesPending) return;

    commit('setLoading', true);
    commit('SET_PACKAGES_PENDING', true);

    try {
      const idToken = await getIdToken();
      const sortPackages = {};

      const { data: packages } = await axios.request({
        method: 'post',
        maxBodyLength: Infinity,
        url: API_URL + '/GetPackages',
        headers: {
          MQTECH_REQUEST_KEY: process.env.VUE_APP_MQ_TECH_API_KEY,
          'Content-Type': 'application/json',
        },
        data: JSON.stringify({ sessionKey: idToken }),
      });

      packages?.tokenpackages?.forEach((pack) => {
        sortPackages[pack.key] = pack;
      });
      commit('setPackages', sortPackages);
    } catch (error) {
      console.error(error);
      commit('setError', error);
    } finally {
      commit('setLoading', false);
      commit('SET_PACKAGES_PENDING', false);
    }
  },

  async searchDisplayName({ commit }, payload) {
    if (payload.searchString?.length < 4) return;

    await getIdToken().then((idToken) => {
      const data = JSON.stringify({
        sessionKey: idToken,
        searchString: payload.searchString,
        excludeUsers: payload.excludeUsers,
      });
      const config = {
        method: 'post',
        maxBodyLength: Infinity,
        url: API_URL + '/searchdisplayname',
        headers: {
          MQTECH_REQUEST_KEY: process.env.VUE_APP_MQ_TECH_API_KEY,
          'Content-Type': 'application/json',
        },
        data,
      };
      return axios
        .request(config)
        .then((response) => {
          commit('setPossibleFriends', response.data.displayNames);
        })
        .catch((error) => {
          console.error(error.response.data.message);
        });
    });
  },

  async sendFriendRequest({ commit, dispatch }, friendToAdd) {
    let responseReturn = null;

    try {
      commit('setLoading', true);

      const sessionKey = await getIdToken();
      const config = {
        method: 'POST',
        url: `${API_URL}/friendrequest`,
        data: { sessionKey, friendToAdd },
      };

      const { data } = await axios.request(config);

      responseReturn = data;

      dispatch('getFriendsList');
    } catch (error) {
      console.error(error.response.data.message);
      responseReturn = error.response.data;
    } finally {
      commit('setLoading', false);
    }

    return responseReturn;
  },

  async acceptFriendRequest({ commit }, friendToAcceptId) {
    let responseReturn = null;

    try {
      commit('setLoading', true);
      const sessionKey = await getIdToken();
      const config = {
        method: 'POST',
        url: `${API_URL}/approvefriend`,
        data: { sessionKey, friendToAcceptId },
      };
      const { data } = await axios.request(config);
      responseReturn = data;
    } catch (error) {
      responseReturn = { status: 'error' };
      console.error(error);
    } finally {
      commit('setLoading', false);
    }

    return responseReturn;
  },

  async removeFriend({ commit }, friendToRemoveId) {
    let responseReturn = null;

    try {
      commit('setLoading', true);

      const sessionKey = await getIdToken();
      const config = {
        method: 'POST',
        url: `${API_URL}/removefriend`,
        data: { sessionKey, friendToRemoveId },
      };
      const { data } = await axios.request(config);
      responseReturn = data;
    } catch (error) {
      console.error(error);
    } finally {
      commit('setLoading', false);
    }

    return responseReturn;
  },

  async rejectFriendRequest({ commit }, friendToDeclineId) {
    let responseReturn = null;

    try {
      commit('setLoading', true);

      const sessionKey = await getIdToken();
      const config = {
        method: 'POST',
        url: `${API_URL}/rejectfriend`,
        data: { sessionKey, friendToDeclineId },
      };
      const { data } = await axios.request(config);
      responseReturn = data;
    } catch (error) {
      console.error(error);
    } finally {
      commit('setLoading', false);
    }

    return responseReturn;
  },

  removeFriendRequestNotification({ dispatch, state }, item) {
    const friendId = item.friendId;
    const alerts = state.alerts;
    const notification = alerts.find((entry) => entry.userId === friendId);
    dispatch('clearsinglealert', notification.key);
  },

  async unblockUser({ commit }, friendToUnblockId) {
    let responseReturn = null;

    try {
      commit('setLoading', true);

      const sessionKey = await getIdToken();
      const config = {
        method: 'POST',
        url: `${API_URL}/unblockfriend`,
        data: { sessionKey, friendToUnblockId },
      };
      const { data } = await axios.request(config);
      responseReturn = data;
    } catch (error) {
      console.error(error);
    } finally {
      commit('setLoading', false);
    }

    return responseReturn;
  },

  async blockUser({ commit }, friendToBlockId) {
    const sessionKey = await getIdToken();
    const config = {
      method: 'POST',
      maxBodyLength: Infinity,
      url: `${API_URL}/blockfriend`,
      headers: {
        MQTECH_REQUEST_KEY: process.env.VUE_APP_MQ_TECH_API_KEY,
        'Content-Type': 'application/json',
      },
      data: { sessionKey, friendToBlockId },
    };

    let reqResponse = null;

    try {
      const { data } = await axios.request(config);
      reqResponse = data;
    } catch (error) {
      console.error(error);
    } finally {
      commit('setLoading', false);
    }

    return reqResponse;
  },

  getFriendsList({ commit }) {
    commit('setLoading', true);

    const userKey = firebase.auth().currentUser?.uid;

    if (!userKey) {
      commit('setLoading', false);
      return;
    }

    commit('setAllUserFriends', []);

    firebase
      .database()
      .ref('FRIEND_SYSTEM')
      .child(userKey)
      .on('child_added', (data) => {
        const friend = data.val();
        friend.online = false;
        commit('appendListOfFriends', friend);
        websocket.emit('subscribeToOnlineStatus', { onlineUsers: [friend.displayName] });
      });
    firebase
      .database()
      .ref('FRIEND_SYSTEM')
      .child(userKey)
      .on('child_changed', (data) => {
        const friend = data.val();
        friend.online = false;
        commit('appendListOfFriends', friend);
        websocket.emit('subscribeToOnlineStatus', { onlineUsers: [friend.displayName] });
      });

    firebase
      .database()
      .ref('FRIEND_SYSTEM')
      .child(userKey)
      .on('child_removed', (data) => {
        const friend = data.val();
        friend.online = false;
        commit('removeFromListOfFriends', friend);
        websocket.emit('unsubscribeToOnlineStatus', { displayNames: [friend.displayName] });
      });

    firebase
      .database()
      .ref('BLOCKED_USERS')
      .child(userKey)
      .on('child_added', (data) => {
        const obj = data.val();
        commit('appendUserBlockedFrom', obj);
      });

    firebase
      .database()
      .ref('BLOCKED_USERS')
      .child(userKey)
      .on('child_changed', (data) => {
        const obj = data.val();
        commit('appendUserBlockedFrom', obj);
      });

    firebase
      .database()
      .ref('BLOCKED_USERS')
      .child(userKey)
      .on('child_removed', (data) => {
        const obj = data.val();
        commit('removeFromListOfUserBlockedFrom', obj);
      });

    commit('setLoading', false);
  },

  stopRealtimeFriendsList() {
    let key = firebase.auth().currentUser?.uid;
    firebase.database().ref('FRIEND_SYSTEM').child(key).off();
  },

  stopRealtimeReferrals(store, displayName) {
    firebase.database().ref('affiliate_system/deposit_wagering').child(displayName).off();
  },

  setPartnerId({ commit }, payload) {
    commit('setPartnerId', payload);
  },

  setPlayMode({ commit }, payload) {
    commit('setPlayerMode', payload.mode);
  },

  async getPlayerInfo({ state, commit, dispatch }) {
    const authData = state.authData;
    const isDevelopment = process.env.NODE_ENV === 'local';

    if (authData?.firstSignin && !isDevelopment) {
      await dispatch('changeSignupStep', SIGN_UP_STEPS.CONGRATS);
      await dispatch('changeSignupDialogState', true);
      await dispatch('changeFirstSignInState', false);
    }

    // commit('SET_FIREBASE_TOKEN', { token: null, expirationTime: null });

    const firebaseToken = await getIdToken({ forceLoad: true });
    const data = JSON.stringify({ sessionKey: firebaseToken });
    const config = {
      method: 'post',
      maxBodyLength: Infinity,
      url: API_URL + '/getPlayerInfo',
      headers: {
        MQTECH_REQUEST_KEY: process.env.VUE_APP_MQ_TECH_API_KEY,
        'Content-Type': 'application/json',
      },
      data,
    };

    commit('setLoading', true);

    try {
      const { data: playerInfo } = await axios.request(config);
      commit('setPlayerInfo', playerInfo);

      if (playerInfo?.profile?.accountStatus === 'suspended') {
        EventBus.emit('user-suspended');
      }

      commit('setLoading', false);
    } catch (error) {
      console.error(error);
    } finally {
      commit('setLoading', false);
    }
  },

  updatePlayerInfo({ commit }, payload) {
    commit('setLoading', true);
    // mrmr profile update
    getIdToken().then((idToken) => {
      const data = JSON.stringify({
        sessionKey: idToken,
        displayName: payload.displayName,
        address: payload.address,
        city: payload.city,
        country: payload.country,
        dateofbirth: payload.dateofbirth,
        email: firebase.auth().currentUser.email,
        firstname: payload.firstname,
        lastname: payload.lastname,
      });
      const config = {
        method: 'post',
        maxBodyLength: Infinity,
        url: API_URL + '/updatePlayerInfo',
        headers: {
          MQTECH_REQUEST_KEY: process.env.VUE_APP_MQ_TECH_API_KEY,
          'Content-Type': 'application/json',
        },
        data,
      };

      axios
        .request(config)
        .then((response) => {
          commit('setPlayerInfo', response.data);
          commit('setLoading', false);
          // we should store it local also
        })
        .catch((error) => {
          console.error(error);
          commit('setError', error);
          commit('setLoading', false);
        });
    });
  },

  async updateCurrentPassword({ commit, dispatch }, { currentPassword, newPassword }) {
    let isSuccessful = false;

    try {
      const sessionKey = await getIdToken();

      commit('setLoading', true);

      const response = await axios.request({
        method: 'POST',
        url: `${API_URL}/updateCurrentPassword`,
        headers: {
          MQTECH_REQUEST_KEY: process.env.VUE_APP_MQ_TECH_API_KEY,
          'Content-Type': 'application/json',
        },
        data: { sessionKey, currentPassword, newPassword },
      });

      if (!response.data || response.data.status !== 'SUCCESS') {
        throw new Error('Failed to update password');
      }

      dispatch('showAlertSnackBar', {
        type: 'success',
        text: response.data.message || 'Password successfully changed',
      });

      isSuccessful = true;
    } catch (error) {
      dispatch('showAlertDialog', {
        title: 'Password was not changed',
        text: error.response?.data?.message || 'Failed to update password',
      });
      console.error(error);
    } finally {
      commit('setLoading', false);
    }

    return { isSuccessful };
  },

  suspendAccount({ commit }) {
    commit('setLoading', true);
    store.dispatch('showConfirmDialog', {
      title: '',
      text: 'Are you sure you want to close your account?',
      yesText: 'Yes',
      callback: () => {
        getIdToken().then((idToken) => {
          let data = JSON.stringify({ sessionKey: idToken });
          let configProfile = {
            method: 'post',
            maxBodyLength: Infinity,
            url: API_URL + '/suspendAccount',
            headers: {
              MQTECH_REQUEST_KEY: process.env.VUE_APP_MQ_TECH_API_KEY,
              'Content-Type': 'application/json',
            },
            data,
          };
          axios
            .request(configProfile)
            .then(() => {
              commit('setLoading', false);
              store.dispatch('showAlertDialog', {
                title: '',
                text: 'We are sorry to see you leave. Your account has been closed.',
              });
              router.push('/');
            })
            .catch((error) => {
              commit('setError', error);
              commit('setLoading', false);
            });
        });
      },
    });
  },

  async getLastPlayedWith({ commit }) {
    commit('setLoading', true);
    let userkey = firebase.auth().currentUser?.uid;

    if (userkey) {
      await firebase
        .database()
        .ref('OPPONENT_SYSTEM')
        .child(userkey)
        .orderByKey()
        .limitToLast(10)
        .on('child_added', (data) => {
          const lastPlayed = data.val();
          lastPlayed.online = false;
          websocket.emit('subscribeToOnlineStatus', { onlineUsers: [lastPlayed.displayName] });
          commit('appendLastPlayedWith', lastPlayed);
        });
      firebase
        .database()
        .ref('OPPONENT_SYSTEM')
        .child(userkey)
        .orderByKey()
        .limitToLast(10)
        .on('child_changed', (data) => {
          const lastPlayed = data.val();
          lastPlayed.online = false;
          websocket.emit('subscribeToOnlineStatus', { onlineUsers: [lastPlayed.displayName] });
          commit('appendLastPlayedWith', lastPlayed);
        });
      firebase
        .database()
        .ref('OPPONENT_SYSTEM')
        .child(userkey)
        .orderByKey()
        .limitToLast(10)
        .on('child_removed', (data) => {
          const lastPlayed = data.val();
          commit('removeFromListOfLastPlayedWith', lastPlayed);
        });
    }
    commit('setLoading', false);
  },

  getCurrentUserID({ commit }) {
    const userKey = getFirebaseUid();
    commit('setCurrentUserId', userKey);
  },

  async getPrivateRoomsByUser({ commit }) {
    const sessionKey = await getIdToken();
    const config = {
      method: 'POST',
      url: `${WALLET_API}/getAllRooms`,
      data: { sessionKey },
    };

    try {
      const { data } = await axios.request(config);

      if (data.hasError || !data.data) {
        throw new Error('An error occurred while getting all rooms');
      }

      commit('SET_PRIVATE_ROOMS', data.data);
    } catch (error) {
      console.error(error);
      commit('setError', error);
    }
  },

  async setLoadingClient(store, payload) {
    const firebaseToken = await getIdToken();

    await axios.request({
      method: 'post',
      maxBodyLength: Infinity,
      url: WALLET_API + '/setPlayerLoadingClient',
      headers: {
        MQTECH_REQUEST_KEY: process.env.VUE_APP_MQ_TECH_API_KEY,
        'Content-Type': 'application/json',
      },
      data: {
        platformId: payload.userId,
        gameId: payload.gameId,
        sessionKey: firebaseToken,
      },
    });
  },

  async declineUnityChallenge(store, payload) {
    await axios.request({
      method: 'post',
      maxBodyLength: Infinity,
      url: `${WALLET_API}/declineInvitationToRoom`,
      headers: {
        MQTECH_REQUEST_KEY: process.env.VUE_APP_MQ_TECH_API_KEY,
        'Content-Type': 'application/json',
      },
      data: {
        gameId: payload.gameId,
        platformId: payload.platformId,
        friendId: payload.friendId,
        roomId: payload.roomId,
      },
    });
  },

  // async requestWithdrawal({ commit }, payload) {
  //   commit("setLoading", true);
  //
  //   await getIdToken().then(async (idToken) => {
  //     let data = JSON.stringify({
  //       sessionKey: idToken,
  //       amount: payload.amount,
  //       send_address: payload.sendaddress,
  //       payment_currency: payload.payment_currency,
  //     });
  //
  //     let config = {
  //       method: "post",
  //       maxBodyLength: Infinity,
  //       url: API_URL + "/requestWithdrawal",
  //       headers: {
  //         MQTECH_REQUEST_KEY: process.env.VUE_APP_MQ_TECH_API_KEY,
  //         "Content-Type": "application/json",
  //       },
  //       data: data,
  //     };
  //
  //     await axios
  //       .request(config)
  //       .then((response) => {
  //         commit("setLoading", false);
  //         commit("setRequestWithdrawal", response.data);
  //       })
  //       .catch((error) => {
  //         console.error(error);
  //         commit("setLoading", false);
  //         commit("setError", error);
  //       });
  //   });
  // },

  async getUserBalance({ commit }) {
    commit('setLoading', true);

    try {
      const firebaseToken = await getIdToken();
      const config = {
        method: 'post',
        maxBodyLength: Infinity,
        url: API_URL + '/getBalance',
        headers: {
          MQTECH_REQUEST_KEY: process.env.VUE_APP_MQ_TECH_API_KEY,
          'Content-Type': 'application/json',
        },
        data: JSON.stringify({
          email: firebase.auth().currentUser.email,
          sessionKey: firebaseToken,
        }),
      };

      const { data } = await axios.request(config);

      if (process.env.NODE_ENV !== 'local' && data?.restricted) {
        if (window.location?.pathname !== '/') {
          await router.push('/');
        }
      } else if (data) {
        commit('setUserDepositBalance', { balance: data.depositBalance });
        //commit('setUserBonusBalance', { balance: data.bonusBalance });
        commit('setUserWithdrawableBalance', { balance: data.withdrawableBalance });
      }
    } catch (error) {
      console.error(error);
      commit('setError', error);
    } finally {
      commit('setIsOlympusAvailable', true);
      commit('setIsMultiPlinkoLimitedAvailable', true);
      commit('setLoading', false);
    }
  },

  async getUserWithdrawals({ commit }) {
    commit('setLoading', true);
    const key = getFirebaseUid();
    const user = await firebase.database().ref(`users/${key}`).once('value');
    const userData = user.val();

    if (userData && userData.withdrawableBalance && userData.withdrawableBalance.withdrawalReservations) {
      commit('withdrawalReservations', userData.withdrawableBalance.withdrawalReservations);
    }
    commit('setLoading', false);
  },

  async requestTransactionHistory({ state, /*dispatch,*/ commit }) {
    commit('setLoading', true);

    if (!state.packages?.length) {
      // await dispatch('getPackages');
    }

    const sessionKey = await getIdToken();
    const data = JSON.stringify({ sessionKey });
    const config = {
      method: 'post',
      maxBodyLength: Infinity,
      url: API_URL + '/getBankingHistory',
      headers: {
        MQTECH_REQUEST_KEY: process.env.VUE_APP_MQ_TECH_API_KEY,
        'Content-Type': 'application/json',
      },
      data,
    };

    try {
      const { data } = await axios.request(config);

      const mapAmount = (item) => {
        if (item.paymentmethod === 'now_payments') {
          return ['finished', 'partially_paid'].includes(item.status) ? item.amount : null;
        }
        return item.amount || null;
      };

      const processPaymentMethod = (item) => (item.paymentmethod || item.method)?.replace(/_/g, '').toLowerCase();

      const mapItem = (item, type, date = null) => ({
        type,
        status: item.status?.toLowerCase() === 'not sent to nowpayments' ? 'Request pending approval' : item.status,
        paymentMethod: processPaymentMethod(item),
        amount: mapAmount(item),
        date: moment(date || item.timestamp).format('YYYY-MM-DD'),
      });

      const purchaseData = Object.entries(data.userpurchases || {}).flatMap(([date, group]) =>
        Object.values(group).flatMap((subgroup) =>
          Object.values(subgroup).map((item) => mapItem(item, FILTER_TYPE.Purchases, date)),
        ),
      );

      const dataEntries = Object.entries(data.withdrawalrequests || {});
      const withdrawalData = dataEntries.map(([, item]) => mapItem(item, FILTER_TYPE.Withdrawals));
      commit('setTransHistory', [...purchaseData, ...withdrawalData].filter(Boolean));
    } catch (error) {
      console.error(error);
      commit('setError', error);
    } finally {
      commit('setLoading', false);
    }
  },

  async getTotalWithdrawalRequests({ commit }) {
    try {
      const idToken = await getIdToken();

      const config = {
        method: 'post',
        maxBodyLength: Infinity,
        url: API_URL + '/getRequestedWithdrawals',
        headers: {
          MQTECH_REQUEST_KEY: process.env.VUE_APP_MQ_TECH_API_KEY,
          'Content-Type': 'application/json',
        },
        data: JSON.stringify({
          sessionKey: idToken,
        }),
      };

      const { data } = await axios.request(config);
      commit('setUserTotalWithdrawalRequests', data.totalWithdrawalRequested);
    } catch (error) {
      console.error(error);
      commit('setError', error);
    }
  },

  getGameHistory({ commit }) {
    commit('setLoading', true);
    getIdToken().then((idToken) => {
      const data = JSON.stringify({
        sessionKey: idToken,
      });
      const config = {
        method: 'post',
        maxBodyLength: Infinity,
        url: API_URL + '/getGameHistory',
        headers: {
          MQTECH_REQUEST_KEY: process.env.VUE_APP_MQ_TECH_API_KEY,
          'Content-Type': 'application/json',
        },
        data,
      };
      axios
        .request(config)
        .then((response) => {
          let gameHistoryData = [];
          const alldata = response.data.alldata;

          for (let key in alldata) {
            gameHistoryData.push({
              gameName: alldata[key].gameName,
              created: alldata[key].created,
              date: new Date(alldata[key].created).toLocaleDateString('en-US', {
                year: 'numeric',
                month: 'short',
                day: 'numeric',
              }),
              total_bet: (alldata[key].total_bet / 100).toFixed(2),
              total_win: (alldata[key].total_win / 100).toFixed(2),
              isMultiplayer: alldata[key].isMultiplayer,
              currency: alldata[key].currency,
              roundId: key,
            });
          }

          commit('setLoading', false);
          commit('setGameHistory', gameHistoryData);
        })
        .catch((error) => {
          console.error(error);
          commit('setLoading', false);
          commit('setError', error);
        });
    });
  },

  /*
  getBonusHistory({ commit }) {
    commit('setLoading', true);
    getIdToken().then((idToken) => {
      let data = JSON.stringify({
        sessionKey: idToken,
      });

      let config = {
        method: 'post',
        maxBodyLength: Infinity,
        url: API_URL + '/getBonusHistory',
        headers: {
          MQTECH_REQUEST_KEY: process.env.VUE_APP_MQ_TECH_API_KEY,
          'Content-Type': 'application/json',
        },
        data: data,
      };
      axios
        .request(config)
        .then((response) => {
          let bonusHistoryData = [];
          const alldata = response.data.alldata;

          for (let key in alldata) {
            const amount =
              alldata[key].bonusId === 'Welcome Bonus' || alldata[key].bonusId === 'Daily Bonus'
                ? alldata[key].startAmount.MMC
                : alldata[key].amount.MMC;
            bonusHistoryData.push({
              title: alldata[key].name ? alldata[key].name : alldata[key].bonusId,
              amount: amount,
              realAmount: alldata[key].amount.MMC,
              unrakedAmount: alldata[key].unrakedAmount.MMC,
              rakedAmount: alldata[key].rakedAmount
                ? alldata[key].rakedAmount.MMC
                : amount - alldata[key].unrakedAmount.MMC,
              validUntil: new Date(alldata[key].expirationDate).toLocaleDateString('en-US', {
                year: 'numeric',
                month: 'short',
                day: 'numeric',
                hour: 'numeric',
                minute: 'numeric',
              }),
              date: new Date(alldata[key].dateCreated).toLocaleDateString('en-US', {
                year: 'numeric',
                month: 'short',
                day: 'numeric',
                hour: 'numeric',
                minute: 'numeric',
              }),
              status: alldata[key].status,
              claimed: alldata[key].claimed,
            });
          }

          commit('setLoading', false);
          commit('setBonusHistory', bonusHistoryData);
        })
        .catch((error) => {
          console.error(error);
          commit('setLoading', false);
          commit('setError', error);
        });
    });
  },
  */

  getMqGameDomain() {
    return MQ_GAME_DOMAIN;
  },

  getGoogleRecaptchaParam() {
    return { script: GOOGLE_RECAPTCHA_SCRIPT, key: GOOGLE_RECAPTCHA_SITE_KEY };
  },

  async acceptchallenge(state, payload) {
    let responseReturn = null;
    stopTitleBlink();
    await getIdToken().then(async (idToken) => {
      const data = JSON.stringify({
        privateRoomId: payload.privateRoomId,
        sessionKey: idToken,
      });
      const config = {
        method: 'post',
        maxBodyLength: Infinity,
        url: API_URL + '/acceptChallenge',
        headers: {
          MQTECH_REQUEST_KEY: process.env.VUE_APP_MQ_TECH_API_KEY,
          'Content-Type': 'application/json',
        },
        data,
      };
      return axios
        .request(config)
        .then((response) => {
          responseReturn = response.data;
        })
        .catch((error) => {
          responseReturn = { haserrors: true };
          console.error(error);
        });
    });
    return responseReturn;
  },

  async declineChallenge(state, payload) {
    let responseReturn = null;
    stopTitleBlink();
    await getIdToken().then(async (idToken) => {
      const data = JSON.stringify({
        privateRoomId: payload.privateRoomId,
        sessionKey: idToken,
      });
      const config = {
        method: 'post',
        maxBodyLength: Infinity,
        url: API_URL + '/declineChallenge',
        headers: {
          MQTECH_REQUEST_KEY: process.env.VUE_APP_MQ_TECH_API_KEY,
          'Content-Type': 'application/json',
        },
        data,
      };
      return axios
        .request(config)
        .then((response) => {
          responseReturn = response.data;
        })
        .catch((error) => {
          responseReturn = { haserrors: true };
          console.error(error);
        });
    });
    return responseReturn;
  },

  async deactivatePrivateRoom(state, payload) {
    if (!payload.isUnity) {
      await getIdToken().then(async (idToken) => {
        const data = JSON.stringify({
          privateRoomId: payload.privateRoomId,
          privateRoomToken: payload.privateRoomToken,
          bankId: payload.bankId,
          sessionKey: idToken,
        });
        const config = {
          method: 'post',
          maxBodyLength: Infinity,
          url: API_URL + '/deactivatePrivateRoom',
          headers: {
            MQTECH_REQUEST_KEY: process.env.VUE_APP_MQ_TECH_API_KEY,
            'Content-Type': 'application/json',
          },
          data,
        };
        return axios.request(config).catch((error) => {
          console.error(error);
        });
      });
    } else {
      let config = {
        method: 'post',
        maxBodyLength: Infinity,
        url: `${WALLET_API}/closePrivateRoom`,
        headers: {
          MQTECH_REQUEST_KEY: process.env.VUE_APP_MQ_TECH_API_KEY,
          'Content-Type': 'application/json',
        },
        data: { gameId: payload.gameId, platformId: getFirebaseUid(), roomId: payload.privateRoomId },
      };
      return axios.request(config).catch((error) => {
        console.error(error);
      });
    }
  },

  async getPrivateRoomID(state, payload) {
    const userKey = getFirebaseUid();
    let privateRoomId = null;
    let privateRoomDbData = null;
    let message = '';

    await getIdToken().then(async (idToken) => {
      const data = JSON.stringify({
        privateRoomToken: payload.privateRoomToken,
        gameId: payload.gameId,
        bankId: payload.bankId,
        buyIn: payload.buyIn,
        hostId: userKey,
        sessionKey: idToken,
      });
      const config = {
        method: 'post',
        maxBodyLength: Infinity,
        url: API_URL + '/getPrivateRoomID',
        headers: {
          MQTECH_REQUEST_KEY: process.env.VUE_APP_MQ_TECH_API_KEY,
          'Content-Type': 'application/json',
        },
        data,
      };
      await axios
        .request(config)
        .then((response) => {
          if (response.data.status === 'ERROR') {
            privateRoomId = null;
            privateRoomDbData = null;
          } else {
            privateRoomId = response.data.privateRoomId;
            privateRoomDbData = response.data.privateRoomData;
          }
        })
        .catch((error) => {
          message = error.response.data.message;
          console.error('error createting private room id', error);
        });
    });
    return {
      message,
      privateRoomId: privateRoomId,
      privateRoomDbData: privateRoomDbData,
    };
  },

  async issueChallenge({ commit }, payload) {
    // issue challenge to the array of displayNames
    let selectedFriends = payload.selectedFriends;
    let privateRoomId = payload.privateRoomDbData.privateRoomId;
    let addFriendUrl = payload.addFriendUrl;
    let privateRoomURL =
      MQ_GAME_DOMAIN +
      '/battlegroundstartprivategame.do?' +
      'mmcToken=' +
      payload.token +
      '&' +
      'mqcToken=' +
      payload.token +
      '&' +
      'privateRoomId=' +
      privateRoomId +
      '&' +
      'lang=en&' +
      'homeUrl=' +
      encodeURIComponent(CHALLENGE_URL) +
      '&' +
      'addFriendUrl=' +
      addFriendUrl;
    commit('setPrivateRoomURL', privateRoomURL);
    await getIdToken().then((idToken) => {
      const data = JSON.stringify({
        selectedFriends: selectedFriends,
        privateRoomDbData: payload.privateRoomDbData,
        sessionKey: idToken,
      });
      const config = {
        method: 'post',
        maxBodyLength: Infinity,
        url: API_URL + '/inviteFriendsPrivateRoom',
        headers: {
          MQTECH_REQUEST_KEY: process.env.VUE_APP_MQ_TECH_API_KEY,
          'Content-Type': 'application/json',
        },
        data,
      };

      return axios.request(config).catch((error) => {
        console.error(error);
      });
    });
  },

  async uploadMetadata(state, payload) {
    const userKey = getFirebaseUid();
    // let filetype = payload.type;
    const storageRef = firebase.storage().ref().child('userkyc').child(userKey);
    // [START storage_upload_metadata]
    // Create file metadata including the content type
    // let metadata = {
    //   contentType: filetype,
    // };
    // store it
    // Upload the file and metadata
    // we must upload it in this manner because of the nature of base64 images. Essentially cuts off the first 23 chars.
    /*let uploadTask = */
    await storageRef
      .child(Date.now() + '.jpg')
      .putString(payload.data, 'data_url')
      .then(function () {
        // dispatch('listCloudDepositImagesAsync', payload)
      });
    // get the list of that node (promise all)
    /*let listTask = */
    storageRef.listAll().then((results) => {
      results.items.forEach((itemRef) => {
        itemRef.getDownloadURL().then(() => {
          // Insert url into an <img v-reload-on-fail> tag to "download"
        });
        // All the items under listRef.
      });
    });
    // get the links for them all

    // add them to the list

    // [END storage_upload_metadata]
  },

  toggleSidebar({ commit }, payload) {
    commit('SET_SIDEBAR', payload);
  },

  async finishGameSession({ commit }, payload = {}) {
    if (!payload.sid) {
      return;
    }
    await getIdToken({ forceLoad: true }).then((idToken) => {
      const data = JSON.stringify({
        sessionKey: idToken,
        sid: payload.sid,
        privateRoomId: payload.privateRoomId,
      });
      const config = {
        method: 'post',
        maxBodyLength: Infinity,
        url: WALLET_API + '/finishgamesession',
        headers: {
          MQTECH_REQUEST_KEY: process.env.VUE_APP_MQ_TECH_API_KEY,
          'Content-Type': 'application/json',
        },
        data,
      };
      return axios.request(config).catch((error) => {
        console.error(error);
      });
    });
    commit('setPrivateRoomID', null);
  },

  changeHomeUrl({ commit }, payload) {
    switch (payload) {
      case 'BTG':
        commit('setHomeUrl', BTG_LOBBY_URL);
        break;
      case 'FF':
        commit('setHomeUrl', FASTFIRE_URL);
        break;
      case 'MBC':
        commit('setHomeUrl', MBC_LOBBY_URL);
        break;
      case 'TMB':
        commit('setHomeUrl', MAXBLAST_URL);
        break;
      case 'MLTP':
        commit('setHomeUrl', MULTIPLINKO_URL);
        break;
      case 'PL':
        commit('setHomeUrl', PLINKO_URL);
        break;
      case 'CAF':
        commit('setHomeUrl', CHALLENGE_URL);
        break;
    }
  },

  changeGameWindow({ commit }, payload) {
    commit('setGameWindow', payload);
  },

  async generateReferralLink({ commit, getters }) {
    let referralLink = '';
    await getIdToken().then(async (idToken) => {
      let userdisplayname = getters.getuser.displayName;
      if (userdisplayname !== undefined && userdisplayname !== 'undefined') {
        const data = JSON.stringify({
          sessionKey: idToken,
          displayName: userdisplayname,
        });
        const config = {
          method: 'post',
          maxBodyLength: Infinity,
          url: API_URL + '/setReferrer',
          headers: {
            MQTECH_REQUEST_KEY: process.env.VUE_APP_MQ_TECH_API_KEY,
            'Content-Type': 'application/json',
          },
          data,
        };
        return axios
          .request(config)
          .then(async (response) => {
            if (response.status === 200) {
              referralLink = window.location.origin + '?playme=' + userdisplayname;
              await commit('setReferralLink', referralLink);
            }
          })
          .catch((error) => {
            console.error(error);
          });
      }
    });
    return referralLink;
  },

  async referFriend(store, { email }) {
    const sessionKey = await getIdToken();
    const data = JSON.stringify({ sessionKey, email });
    const config = {
      method: 'post',
      maxBodyLength: Infinity,
      url: API_URL + '/sendReferralLink',
      headers: {
        MQTECH_REQUEST_KEY: process.env.VUE_APP_MQ_TECH_API_KEY,
        'Content-Type': 'application/json',
      },
      data,
    };

    let result = null;

    try {
      const { data } = await axios.request(config);
      result = data;
    } catch (error) {
      result = { error: true, errorMessage: error?.response?.data || 'Unknown error' };
      console.error(error?.response?.data);
    }

    return result;
  },

  async getReferralLink({ commit, getters }) {
    commit('setLoading', true);
    let referralLinkExists = false;
    let userdisplayname = '';
    await getIdToken().then(async (idToken) => {
      userdisplayname = getters.getuser.displayName;

      if (userdisplayname) {
        const data = JSON.stringify({
          sessionKey: idToken,
          displayName: userdisplayname,
        });
        const config = {
          method: 'post',
          maxBodyLength: Infinity,
          url: API_URL + '/checkReferralLinkExists',
          headers: {
            MQTECH_REQUEST_KEY: process.env.VUE_APP_MQ_TECH_API_KEY,
            'Content-Type': 'application/json',
          },
          data,
        };
        await axios
          .request(config)
          .then((response) => {
            referralLinkExists = response.data?.referralLinkExists;
          })
          .catch((error) => {
            console.error(error);
          });
      }
    });
    if (referralLinkExists) {
      let referralLink = window.location.origin + '?playme=' + userdisplayname;
      await commit('setReferralLink', referralLink);
      commit('setLoading', false);
    }
    commit('setLoading', false);
  },

  getReferrals({ commit, getters }) {
    commit('setLoading', true);
    let displayName = getters.getuser.displayName;
    if (displayName) {
      commit('setReferrals', []);
      firebase
        .database()
        .ref('affiliate_system/deposit_wagering')
        .child(displayName)
        .child('usersReferred')
        .on('child_added', (data) => {
          if (data.val().inviteAccepted) {
            commit('appendReferrals', data.val());
          }
        });
      firebase
        .database()
        .ref('affiliate_system/deposit_wagering')
        .child(displayName)
        .child('usersReferred')
        .on('child_changed', (data) => {
          if (data.val().inviteAccepted) {
            commit('appendReferrals', data.val());
          }
        });

      firebase
        .database()
        .ref('affiliate_system/deposit_wagering')
        .child(displayName)
        .child('usersReferred')
        .on('child_removed', (data) => {
          commit('removeReferral', data.val());
        });
    }
    commit('setLoading', false);
  },

  /**
   * Set the wallet dialog
   * @param commit
   * @param payload
   *   @property {boolean} value - A boolean indicating the state of the dialog
   *   @property {('purchase'|'withdrawal')} tab - A string indicating the active tab in the dialog
   */
  setWalletDialog({ commit }, payload) {
    commit('setWalletDialog', payload);
  },

  async getSumsubAccessToken({ commit }) {
    const isProd = process.env.NODE_ENV === 'production';

    await getIdToken().then(async (idToken) => {
      const url = isProd ? `${SUMSUB_API}/getAccessToken` : `${API_URL}/kyc/getaAccessToken`;

      let config = {
        method: 'POST',
        maxBodyLength: Infinity,
        url,
        headers: {
          'Content-Type': 'application/json',
          mqtech_request_key: process.env.VUE_APP_MQ_TECH_API_KEY,
        },
        data: {
          sessionKey: idToken,
        },
      };
      await axios
        .request(config)
        .then((response) => {
          commit('setSumsubAccessToken', response.data.token);
        })
        .catch((error) => {
          console.error(error);
        });
    });
  },

  async resendVerificationEmail(store, payload) {
    let config = {
      method: 'post',
      url: API_URL + '/resendVerificationCode',
      headers: {
        MQTECH_REQUEST_KEY: process.env.VUE_APP_MQ_TECH_API_KEY,
        'Content-Type': 'application/json',
      },
      data: { email: payload.email },
    };
    await axios
      .request(config)
      .then(async (response) => {
        if (response.data.haserror) {
          store.dispatch('showAlertDialog', {
            title: '',
            text: response.data.message,
          });
        } else {
          store.dispatch('showAlertDialog', {
            title: response.data.message,
            text: 'Check your mail, please!',
          });
        }
      })
      .catch((error) => {
        store.dispatch('showAlertDialog', {
          title: 'Something went wrong',
          text: error.response.data.message,
        });
      });
  },

  async setLoggedIn({ state, commit, dispatch }, isAutoSignIn) {
    if (state.isLoggedIn) return;

    if (state.authData.ignoreLogin) {
      // the session will be set in another tab where the user has just confirmed their email address
      commit('SET_LOGGED_IN', true);
      commit('SET_IGNORE_LOGIN', false);
      return;
    }

    try {
      const sessionKey = await getIdToken();

      if (!sessionKey) {
        console.warn(`setLoggedIn: sessionKey not found`);
        return;
      }

      const data = JSON.stringify({ sessionKey, isAutoSignIn });

      const config = {
        method: 'post',
        maxBodyLength: Infinity,
        url: API_URL + '/setLoggedIn',
        headers: {
          MQTECH_REQUEST_KEY: process.env.VUE_APP_MQ_TECH_API_KEY,
          'Content-Type': 'application/json',
        },
        data,
      };

      const response = await axios.request(config);
      const session = response?.data?.session;

      if (session) {
        localStorage.setItem('session', session);
        commit('SET_LOGGED_IN', true);
      }

      if (state.user?.balance) {
        await dispatch('getUserBalance');
      }
    } catch (error) {
      console.error(error);
      commit('SET_LOGGED_IN', false);
    }
  },

  getOnlinePlayersFromList(store, playerNames) {
    websocket.emit('subscribeToOnlineStatus', { onlineUsers: playerNames });
  },

  async getCurrencies({ state, commit }) {
    if (state.cashierCurrencies.length) {
      return state.cashierCurrencies;
    }

    const sessionKey = await getIdToken();
    const config = {
      method: 'get',
      url: API_URL + '/currencies',
      headers: {
        MQTECH_REQUEST_KEY: process.env.VUE_APP_MQ_TECH_API_KEY,
        'Content-Type': 'application/json',
      },
      data: { sessionKey },
    };

    try {
      const { data } = await axios.request(config);

      if (data.haserror) {
        throw new Error('getCurrencies: The server responded with an error');
      }

      commit('SET_CASHIER_CURRENCIES', data.data);

      return data.data;
    } catch (error) {
      console.error(error);
    }
  },

  async getEstimate(_, payload) {
    const firebaseToken = await getIdToken();

    const config = {
      method: 'post',
      url: NOW_PAYMENT_API + '/estimate',
      data: { ...payload, sessionKey: firebaseToken },
    };

    return axios.request(config);
  },

  async getEstimatePayout(_, payload) {
    const firebaseToken = await getIdToken();

    const config = {
      method: 'post',
      url: API_URL + '/estimatePayout',
      headers: {
        MQTECH_REQUEST_KEY: process.env.VUE_APP_MQ_TECH_API_KEY,
        'Content-Type': 'application/json',
      },
      data: { ...payload, sessionKey: firebaseToken },
    };

    return axios.request(config);
  },

  async requestWithdrawal(_, payload) {
    const sessionKey = await getIdToken();

    const config = {
      method: 'post',
      url: API_URL + '/requestWithdrawal',
      headers: {
        MQTECH_REQUEST_KEY: process.env.VUE_APP_MQ_TECH_API_KEY,
        'Content-Type': 'application/json',
      },
      data: { ...payload, sessionKey },
    };

    return axios.request(config);
  },

  async requestPaymentInvoice({ commit }, payload) {
    const controller = new AbortController();
    const sessionKey = await getIdToken();

    const config = {
      method: 'post',
      url: API_URL + '/requestInvoice',
      headers: {
        MQTECH_REQUEST_KEY: process.env.VUE_APP_MQ_TECH_API_KEY,
        'Content-Type': 'application/json',
      },
      data: { ...payload, sessionKey },
      signal: controller.signal,
    };

    commit('SET_INVOICE_ABORT_CTRL', controller);

    return axios.request(config);
  },

  cancelInvoiceRequest({ state, commit }) {
    if (!state.invoiceAbortCtrl) return;
    state.invoiceAbortCtrl.abort?.();
    commit('SET_INVOICE_ABORT_CTRL', null);
  },

  // async getAvailableBonuses({ commit }) {
  //   try {
  //     const idToken = await getIdToken();
  //     const data = JSON.stringify({
  //       sessionKey: idToken,
  //     });
  //     const config = {
  //       method: 'post',
  //       maxBodyLength: Infinity,
  //       url: API_URL + '/findAllAvailableBonuses',
  //       headers: {
  //         MQTECH_REQUEST_KEY: process.env.VUE_APP_MQ_TECH_API_KEY,
  //         'Content-Type': 'application/json',
  //       },
  //       data: data,
  //     };
  //     const response = await axios.request(config);
  //     const availableBonuses = response.data.availableBonuses;
  //     commit('setAvailableBonuses', availableBonuses);
  //   } catch (error) {
  //     console.error(error);
  //     commit('setError', error);
  //   }
  // },

  // async getAllActiveBonuses({ commit }) {
  //   try {
  //     const idToken = await getIdToken();
  //     const data = JSON.stringify({
  //       sessionKey: idToken,
  //     });
  //     const config = {
  //       method: 'post',
  //       maxBodyLength: Infinity,
  //       url: API_URL + '/findAllActiveBonuses',
  //       headers: {
  //         MQTECH_REQUEST_KEY: process.env.VUE_APP_MQ_TECH_API_KEY,
  //         'Content-Type': 'application/json',
  //       },
  //       data: data,
  //     };
  //     const response = await axios.request(config);
  //     const allActiveBonuses = response.data.allActiveBonuses;
  //     commit('setAllActiveBonuses', allActiveBonuses);
  //   } catch (error) {
  //     console.error(error);
  //     commit('setError', error);
  //   }
  // },

  // async getUnclaimedBonuses({ commit }) {
  //   try {
  //     const idToken = await getIdToken();
  //     const data = JSON.stringify({
  //       sessionKey: idToken,
  //     });
  //     const config = {
  //       method: 'post',
  //       maxBodyLength: Infinity,
  //       url: API_URL + '/findAllUnclaimedBonuses',
  //       headers: {
  //         MQTECH_REQUEST_KEY: process.env.VUE_APP_MQ_TECH_API_KEY,
  //         'Content-Type': 'application/json',
  //       },
  //       data: data,
  //     };
  //     const response = await axios.request(config);
  //     const unclaimedBonuses = response.data.unclaimedBonuses;
  //     commit('setUnclaimedBonuses', unclaimedBonuses);
  //   } catch (error) {
  //     console.error(error);
  //     commit('setError', error);
  //   }
  // },

  // async claimPrizes({ commit, getters }) {
  //   try {
  //     const idToken = await getIdToken();
  //     const unclaimedBonuses = getters.getUnclaimedBonuses;
  //
  //     for (const unclaimedBonus of unclaimedBonuses) {
  //       let data = JSON.stringify({
  //         sessionKey: idToken,
  //         bonusLogId: unclaimedBonus.bonusLogId,
  //       });
  //
  //       let config = {
  //         method: 'post',
  //         url: API_URL + '/claimBonus',
  //         headers: {
  //           MQTECH_REQUEST_KEY: process.env.VUE_APP_MQ_TECH_API_KEY,
  //           'Content-Type': 'application/json',
  //         },
  //         data,
  //       };
  //
  //       try {
  //         await axios.request(config);
  //       } catch (error) {
  //         console.error(error);
  //         commit('setError', error);
  //       }
  //     }
  //
  //     await Promise.all([store.dispatch('getAvailableBonuses'), store.dispatch('getUnclaimedBonuses')]);
  //   } catch (error) {
  //     console.error(error);
  //   }
  // },

  async sendVerification({ commit, dispatch }, { phoneNumber, ignoreRedirect = false }) {
    const firebaseToken = await getIdToken({ forceLoad: true });
    const config = {
      method: 'post',
      url: API_URL + '/sendSms',
      headers: {
        MQTECH_REQUEST_KEY: process.env.VUE_APP_MQ_TECH_API_KEY,
        'Content-Type': 'application/json',
      },
      data: { sessionKey: firebaseToken, phoneNumber },
    };

    commit('setLoading', true);

    try {
      await axios.request(config);

      if (!ignoreRedirect) {
        dispatch('changeSignupStep', SIGN_UP_STEPS.OTP_VERIFICATION);
      }
    } catch (error) {
      if (error.response?.status === 409) {
        dispatch('showAlertDialog', {
          title: 'Phone Number Already Used',
          text: 'This phone number is already associated with another account. Please use a different number.',
        });
      } else {
        dispatch('showAlertDialog', {
          title: 'The phone number you entered seems to be invalid.',
          text: 'Please double-check the number and try again.',
        });
      }
    } finally {
      commit('setLoading', false);
    }
  },

  async setEmailVerified({ commit }, email) {
    commit('setLoading', true);

    const config = {
      method: 'post',
      maxBodyLength: Infinity,
      url: API_URL + '/setEmailVerified',
      headers: {
        MQTECH_REQUEST_KEY: process.env.VUE_APP_MQ_TECH_API_KEY,
        'Content-Type': 'application/json',
      },
      data: JSON.stringify({
        email: email,
      }),
    };

    await axios
      .request(config)
      .then(() => {
        commit('setLoading', false);
      })
      .catch((error) => {
        console.error(error);
        store.dispatch('showAlertDialog', {
          title: 'Error',
          text: error.response.data.status || error.response.data.message,
        });
        commit('setLoading', false);
        commit('setError', error);
      });
  },

  async verifyPhoneNumber({ commit, dispatch }, payload) {
    const firebaseToken = await getIdToken();
    const config = {
      method: 'post',
      url: API_URL + '/verifyPhoneNumber',
      headers: {
        MQTECH_REQUEST_KEY: process.env.VUE_APP_MQ_TECH_API_KEY,
        'Content-Type': 'application/json',
      },
      data: {
        verificationKey: payload.verificationKey || '345919',
        phoneNumber: payload.phoneNumber,
        sessionKey: firebaseToken,
      },
    };
    let response = null;

    commit('setLoading', true);

    try {
      response = await axios.request(config);
      dispatch('changeSignupStep', SIGN_UP_STEPS.CONGRATS);
    } catch (error) {
      dispatch('showAlertDialog', {
        title: 'The OTP you entered is incorrect. Please try again.',
        text: error.response.data.message,
      });
    } finally {
      commit('setLoading', false);
    }

    return response;
  },

  subscribeToOrderStatus({ commit }, orderPath) {
    const db = firebase.database();
    const ref = db.ref(orderPath);
    orderStatusRef = db.ref(orderPath);

    ref.on(
      'value',
      (snapshot) => {
        if (snapshot.exists()) {
          commit('setOrderStatus', snapshot.val().status);
        }
      },
      (error) => {
        console.error('Error getting document:', error);

        commit('setOrderStatus', null);
      },
    );
  },

  unsubscribeFromOrderStatus({ commit }) {
    if (orderStatusRef) {
      orderStatusRef.off();

      commit('setOrderStatus', null);
    }
  },

  async sendContactForm(store, formData) {
    const config = {
      headers: {
        MQTECH_REQUEST_KEY: process.env.VUE_APP_MQ_TECH_API_KEY,
        Accept: 'application/json',
        'Content-Type': 'multipart/form-data',
      },
    };

    try {
      await axios.post(API_URL + '/sendContactFormEmail', formData, config);
      store.dispatch('showConfirmDialog', {
        title: 'We have received your query',
        text: 'and will aim to respond within 48 hours',
        yesText: 'OK',
        noVisible: false,
      });
      return { error: false };
    } catch (error) {
      console.error('Error:', error);
      store.dispatch('showAlertDialog', {
        title: 'Something went wrong',
        text: error.response.data.message || 'Unknown error occurred',
      });
      return { error: true };
    }
  },

  async transferRewardToBalance({ commit, getters, dispatch }, amount) {
    const sessionKey = await getIdToken();
    const userId = getters.getuser.id;
    const config = {
      method: 'post',
      maxBodyLength: Infinity,
      url: `${API_URL}/transferRewardToBalance`,
      headers: {
        MQTECH_REQUEST_KEY: process.env.VUE_APP_MQ_TECH_API_KEY,
        'Content-Type': 'application/json',
      },
      data: JSON.stringify({
        userId,
        sessionKey,
        amount: amount * 100,
      }),
    };

    commit('setLoading', true);

    try {
      const { data } = await axios.request(config);

      dispatch('showAlertSnackBar', { text: data.status });

      if (!data.haserror) {
        commit('setUserReferralRewardData', { balance: 0, count: 0 });
      }
    } catch (error) {
      console.error(error);
      commit('setError', error);
      dispatch('showAlertDialog', {
        title: 'Insufficient balance',
        text: error.response.data.status || error.response.data.message,
      });
    } finally {
      commit('setLoading', false);
    }
  },

  async getReferralRewardsData({ state, commit }) {
    if (state.pendingFlags.getReferralRewardsData) return;

    const firebaseToken = await getIdToken();
    const email = firebase.auth().currentUser.email;
    const config = {
      method: 'post',
      maxBodyLength: Infinity,
      url: API_URL + '/getRefData',
      headers: {
        MQTECH_REQUEST_KEY: process.env.VUE_APP_MQ_TECH_API_KEY,
        'Content-Type': 'application/json',
      },
      data: JSON.stringify({ sessionKey: firebaseToken, email }),
    };

    commit('setLoading', true);
    commit('SET_PENDING_FLAG', ['getReferralRewardsData', true]);

    try {
      const { data } = await axios.request(config);

      if (!data) {
        throw new Error('The server responded with an invalid or empty data object');
      }

      if (data.hasError) {
        throw new Error('The server responded with an error');
      }

      if (typeof data.refBalance !== 'number') {
        throw new Error('The server responded with an invalid refBalance value');
      }

      commit('setUserReferralRewardData', { balance: data.refBalance, count: data.refCount });
    } catch (error) {
      console.error(`getReferralRewardsData error: ${error}`);
    } finally {
      commit('setLoading', false);
      commit('SET_PENDING_FLAG', ['getReferralRewardsData', false]);
    }
  },

  async loadGlobalAffiliateConfig({ commit }) {
    commit('setGlobalAffiliateConfig', null);

    const snapshot = await firebase.database().ref('affiliate_system').child('global_config').once('value');
    const data = snapshot.val();

    let affiliateConfigData = {
      affiliatesEarnPercentage: 0,
      minAmountTransfer: 0,
      minAmountWithdraw: 0,
    };

    if (data) {
      affiliateConfigData = {
        affiliatesEarnPercentage: data?.affiliatesEarnPercentage ?? 0,
        minAmountTransfer: data.minAmountTransfer ?? 0,
        minAmountWithdraw: data.minAmountWithdraw ?? 0,
      };
    }

    commit('setGlobalAffiliateConfig', affiliateConfigData);
  },

  async loadGlobalConfig({ commit }) {
    const snapshot = await firebase.database().ref('config/global').once('value');
    const data = snapshot.val();

    let globalConfig = {
      maximumRedemptionWithoutKYC: 150,
    };

    if (data) {
      globalConfig = {
        maxAmountWithoutKyc: data.maximumRedemptionWithoutKYC ?? 150,
      };
    }

    commit('setGlobalConfig', globalConfig);
  },

  async getMyTotalReferralRewards({ getters }) {
    const userId = getters.getuser.id;

    return firebase
      .database()
      .ref(`affiliate_system/rewards/byaffiliate/${userId}`)
      .once('value')
      .then((snapshot) => {
        if (!snapshot.exists()) {
          return 0;
        }

        const value = snapshot.val();

        return Object.values(value).reduce((total, obj) => {
          return (
            total +
            Object.values(obj).reduce((subTotal, subObj) => {
              return subTotal + (subObj.totalsignupMMC || 0);
            }, 0)
          );
        }, 0);
      })
      .catch((error) => {
        console.error(error);
      });
  },

  async loadUserBoughtPackages({ commit }) {
    const userId = firebase.auth().currentUser?.uid;
    if (!userId) return;

    const packagesSnapshot = await firebase.database().ref(`TOKENPURCHASES/${userId}`).once('value');
    const packages = packagesSnapshot.val();
    commit('setUserBoughtPackages', packages ?? {});
  },

  async saveGameSession(store, payload) {
    await getIdToken().then(async (idToken) => {
      const data = JSON.stringify({
        sessionKey: idToken,
        SID: payload.sid,
        privateRoomId: payload.privateRoomId,
      });
      const config = {
        method: 'post',
        maxBodyLength: Infinity,
        url: API_URL + '/addGameSession',
        headers: {
          MQTECH_REQUEST_KEY: process.env.VUE_APP_MQ_TECH_API_KEY,
          'Content-Type': 'application/json',
        },
        data,
      };
      return axios.request(config).catch((error) => {
        console.error(error);
      });
    });
  },

  async isEmailVerified(store, payload) {
    try {
      const data = JSON.stringify({
        email: payload,
      });

      const config = {
        method: 'post',
        maxBodyLength: Infinity,
        url: API_URL + '/isEmailVerified',
        headers: {
          MQTECH_REQUEST_KEY: process.env.VUE_APP_MQ_TECH_API_KEY,
          'Content-Type': 'application/json',
        },
        data,
      };

      const response = await axios.request(config);
      return response.data?.emailVerified || false;
    } catch (error) {
      console.error('Error checking email verification:', error);
      return false;
    }
  },

  async initWebSocket({ state, commit, dispatch }, options = {}) {
    const onBalanceChanged = (dataJSON) => {
      try {
        if (!state.user) return;

        const data = JSON.parse(dataJSON);

        if (!data) {
          websocket.isDebugEnabled() && console.error(`WebSocket: incoming data is not valid: ${dataJSON}`);
          return;
        }

        if (data.userKey !== state.user.id) {
          websocket.isDebugEnabled() &&
            console.error(`WebSocket: incoming data is not associated with the current user: ${dataJSON}`);
          return;
        }

        if (data.balance) {
          const depositBalance = {};

          if (typeof data.balance.MMC !== 'undefined') {
            depositBalance.MMC = parseInt(data.balance.MMC, 10);
          } else {
            depositBalance.MMC = state.depositBalance?.balance?.MMC || 0;
          }

          if (typeof data.balance.MQC !== 'undefined') {
            depositBalance.MQC = parseInt(data.balance.MQC, 10);
          } else {
            depositBalance.MQC = state.depositBalance?.balance?.MQC || 0;
          }

          commit('setUserDepositBalance', { balance: depositBalance });
        }

        if (data.withdrawableBalance) {
          const withdrawableBalance = {};

          if (typeof data.withdrawableBalance.MMC !== 'undefined') {
            withdrawableBalance.MMC = parseInt(data.withdrawableBalance.MMC, 10);
          } else {
            withdrawableBalance.MMC = state.withdrawableBalance?.balance?.MMC || 0;
          }

          if (typeof data.withdrawableBalance.MQC !== 'undefined') {
            withdrawableBalance.MQC = parseInt(data.withdrawableBalance.MQC, 10);
          } else {
            withdrawableBalance.MQC = state.withdrawableBalance?.balance?.MQC || 0;
          }

          commit('setUserWithdrawableBalance', { balance: withdrawableBalance });
        }

        // if (data.bonusBalance) {
        //   const bonusBalance = {};

        //   if (typeof data.bonusBalance.MMC !== 'undefined') {
        //     bonusBalance.MMC = parseInt(data.bonusBalance.MMC, 10);
        //   } else {
        //     bonusBalance.MMC = state.bonusBalance?.balance?.MMC || 0;
        //   }

        //   if (typeof data.bonusBalance.MQC !== 'undefined') {
        //     bonusBalance.MQC = parseInt(data.bonusBalance.MQC, 10);
        //   } else {
        //     bonusBalance.MQC = state.bonusBalance?.balance?.MQC || 0;
        //   }

        //   commit('setUserBonusBalance', { balance: bonusBalance });
        // }
      } catch (error) {
        console.error(`WebSocket: failed to parse incoming data: ${dataJSON}`);
        console.error(`WebSocket: detailed error - ${error}`);
      }
    };

    const onSocketInit = async () => {
      const sessionKey = await getIdToken({ forceLoad: true });
      websocket.emit('auth', { sessionKey });

      websocket.off('balance');
      websocket.off('session');
      websocket.off('profile');
      websocket.off('bonus');
      websocket.off('vip');

      websocket.on('balance', onBalanceChanged);

      websocket.on('online', (update) => {
        const { displayName, isOnline } = update;

        commit('updateFriendOnlineStatus', { displayName, isOnline });
        commit('updateChallengeOnlineStatus', { displayName, isOnline });
        commit('updateAlertOnlineStatus', { displayName, isOnline });
        commit('updateLastPlayedWithOnlineStatus', { displayName, isOnline });

        if (isOnline) {
          commit('appendOnlineUsers', displayName);
        } else {
          commit('removeOnlineUser', displayName);
        }
      });

      websocket.on('profile', ({ emailVerified, phoneVerified }) => {
        if (emailVerified) EventBus.emit('email-verified');
        if (phoneVerified) EventBus.emit('phone-verified');
      });

      websocket.on('bonus', (message) => dispatch('welcome/syncWelcomeBonus', message));

      websocket.on('vip', (message) => dispatch('vip/processVipSocketEvent', message));
    };

    websocket.init(options, onSocketInit);
  },

  destroyWebSocket() {
    websocket.disconnect();
  },

  async sendGaEvent(store, payload) {
    const sessionKey = await getIdToken();
    const config = {
      method: 'post',
      maxBodyLength: Infinity,
      url: API_URL + payload.endpoint,
      headers: {
        MQTECH_REQUEST_KEY: process.env.VUE_APP_MQ_TECH_API_KEY,
      },
      data: {
        sessionKey,
        ...payload?.body,
      },
    };

    try {
      return await axios.request(config);
    } catch (error) {
      console.error(`sendGaEvent error: ${error}`);
    }
  },

  async checkUsernameAvailability(store, displayName) {
    const config = {
      method: 'post',
      maxBodyLength: Infinity,
      url: API_URL + '/displayNameExist',
      headers: {
        MQTECH_REQUEST_KEY: process.env.VUE_APP_MQ_TECH_API_KEY,
      },
      data: {
        displayName,
      },
    };

    try {
      return await axios.request(config);
      // return new Promise((resolve) => {
      //   setTimeout(() => {
      //     resolve({ data: { hasError: Math.random() > 0.5, message: '' } });
      //   }, 1000);
      // });
    } catch (error) {
      console.error(`sendGaEvent error: ${error}`);
    }
  },

  async checkEmailAvailability(store, email) {
    const config = {
      method: 'post',
      maxBodyLength: Infinity,
      url: API_URL + '/emailExist',
      headers: {
        MQTECH_REQUEST_KEY: process.env.VUE_APP_MQ_TECH_API_KEY,
      },
      data: {
        email,
      },
    };

    try {
      return await axios.request(config);
    } catch (error) {
      console.error(`sendGaEvent error: ${error}`);
    }
  },

  async savePayPalOrderData(store, payload) {
    const sessionKey = await getIdToken();
    const config = {
      method: 'post',
      url: API_URL + '/savePayPalOrderData',
      headers: {
        MQTECH_REQUEST_KEY: process.env.VUE_APP_MQ_TECH_API_KEY,
      },
      data: {
        sessionKey,
        ...payload,
      },
    };

    try {
      return await axios.request(config);
    } catch (error) {
      console.error(`savePayPalOrderData error: ${error}`);
    }
  },

  closeCashierDialog({ commit }) {
    commit('setWalletDialog', { value: false, tab: 'purchase' });
  },

  toggleLeftNavbar({ state, commit }) {
    commit('SET_LEFT_NAVBAR_OPEN', !state.leftNavbarOpen);
  },

  setLeftNavbarState({ commit }, value) {
    commit('SET_LEFT_NAVBAR_OPEN', value);
  },

  async claimBonuses({ dispatch }, bonusIds = []) {
    const sessionKey = await getIdToken();
    const config = {
      method: 'post',
      url: `${API_URL}/bonuses/claim`,
      data: { sessionKey, ids: bonusIds },
    };

    try {
      const { data } = await axios.request(config);

      if (data.hasError) {
        throw new Error('Failed to claim bonuses');
      }
    } catch (error) {
      dispatch(
        'showAlertDialog',
        {
          title: 'Failed to claim bonuses',
          text: error.response?.data?.message || 'Unknown error',
        },
        { root: true },
      );
      console.error(error);
    }
  },
};
